import { Lock, LockOpen } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useUpdateTask } from '@/hooks/useTasks';
import type { TaskDetail } from '@/lib/api/types';
import { useT } from '@/lib/i18n';

/**
 * The guard on a task too big to close by reflex, and the release it gates.
 *
 * Both live in the detail on purpose: this is the one screen showing the steps
 * and the history, which is what makes closing a project a decision rather
 * than a reflex. Everywhere else — a row in a list, a checkbox in the menu bar
 * — the tick simply refuses until this has been done.
 */
export function CloseGuard({ task }: { task: TaskDetail }) {
  const t = useT();
  const update = useUpdateTask(task.id);

  return (
    <section className="rounded-xl border border-border bg-surface-sunken p-3">
      <label className="flex items-center gap-2.5 text-sm text-content">
        <input
          type="checkbox"
          checked={task.close_protected}
          onChange={(event) => update.mutate({ close_protected: event.target.checked })}
          className="h-4 w-4 accent-accent"
        />
        <Lock className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
        {t('task.protected.toggle')}
      </label>

      {task.close_protected ? (
        <div className="mt-2.5 pl-7">
          {task.close_armed ? (
            <p className="flex items-center gap-1.5 text-xs text-accent">
              <LockOpen className="h-3.5 w-3.5" aria-hidden />
              {t('task.armed')}
            </p>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              disabled={task.blocked_by_subtasks || update.isPending}
              title={task.blocked_by_subtasks ? t('task.blockedBySubtasks') : undefined}
              onClick={() => update.mutate({ close_armed: true })}
            >
              <LockOpen className="h-3.5 w-3.5" aria-hidden />
              {t('task.arm')}
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}
